import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  AnnouncementSummary,
  MarkReadResponse,
} from '@df-gate-share/shared';
import {
  ANNOUNCEMENT_CATEGORY_LABELS,
  ANNOUNCEMENT_SEVERITY_LABELS,
} from '@df-gate-share/shared';
import { apiClient } from '../lib/api';
import { useAnnouncements } from '../contexts/AnnouncementContext';
import {
  CATEGORY_BADGE_CLASS,
  SEVERITY_BADGE_CLASS,
  formatAnnouncementDate,
} from '../pages/announcementDisplay';

/**
 * 重要お知らせモーダル（Requirement 18.4）
 * ログイン後の初回表示時に、重要度 high かつ未読のお知らせを強い注目喚起表示で提示。
 * 閉じる操作で該当お知らせを既読化（read API 呼び出し）。
 *
 * 複数の高優先度未読がある場合は 1 件ずつ順番に提示する。
 */
const AnnouncementModal: React.FC = () => {
  const navigate = useNavigate();
  const { highPriorityUnread, applyRead } = useAnnouncements();
  const [dismissing, setDismissing] = useState(false);

  // 先頭の高優先度未読を対象にする。既読化されると Context から除去され、
  // 次の対象が繰り上がる。全て既読になれば非表示。
  const current: AnnouncementSummary | undefined = highPriorityUnread[0];

  if (!current) {
    return null;
  }

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      const response = await apiClient.post(
        `/api/v1/announcements/${current.announcementId}/read`
      );
      const data: MarkReadResponse = response.data;
      applyRead(current.announcementId, data.unreadCount ?? 0);
    } catch {
      // 既読化失敗時もローカルからは除去して先へ進める（次回ログインで再提示され得る）。
      applyRead(current.announcementId, Math.max(0, highPriorityUnread.length - 1));
    } finally {
      setDismissing(false);
    }
  };

  const viewDetail = async () => {
    await dismiss();
    navigate('/announcements');
  };

  return (
    <div className="modal-overlay">
      <div className="modal" role="dialog" aria-modal="true">
        <div className="flex items-center gap-2 mb-2">
          <span
            className={`badge ${SEVERITY_BADGE_CLASS[current.severity]}`}
          >
            {ANNOUNCEMENT_SEVERITY_LABELS[current.severity]}
          </span>
          <span
            className={`badge ${CATEGORY_BADGE_CLASS[current.category]}`}
          >
            {ANNOUNCEMENT_CATEGORY_LABELS[current.category]}
          </span>
        </div>
        <h2 className="modal__title">{current.title}</h2>
        <p className="text-muted mb-6" style={{ fontSize: 'var(--text-sm)' }}>
          {formatAnnouncementDate(current.publishAt)}
        </p>
        <div className="modal__actions">
          <button
            onClick={dismiss}
            disabled={dismissing}
            className="btn btn--secondary"
          >
            閉じる
          </button>
          <button
            onClick={viewDetail}
            disabled={dismissing}
            className="btn btn--primary"
          >
            お知らせを見る
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementModal;
